//popup modal for editing an item
import Modal from 'react-bootstrap/Modal';
import Button from "react-bootstrap/esm/Button";
import { CircleX, Tag, Upload, X } from 'lucide-react';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import UpdateProductApi from "../../../services/products/updateProducts.js"
import style from "./EditItemModal.module.scss"
import handleErrorToast from '../../../utils/handleErrorToasts.js';
import { useEffect, useState, useCallback, useRef } from 'react';
import ITEM_TOASTS from '../../../utils/constants/toastMesages/itemToastMessage.js';
import fetchCategories from '../../../services/categories/fetchCategories.js';
import createCategory from '../../../services/categories/createCategory.js';

/**
 * Modal component for editing product details.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.show - Controls the visibility of the modal
 * @param {Function} props.onHide - Function to close the modal
 * @param {Object} props.itemDetails - Current details of the product to edit
 * @param {Function} props.setItemDetails - Setter function to update product details in state
 * @returns {JSX.Element} A modal for editing an existing product
 *
 * @example
 * <EditProductModal
 *   show={true}
 *   onHide={() => setShow(false)}
 *   itemDetails={{ name: 'Burger', price: 100 }}
 *   setItemDetails={setItemDetails}
 * />
 * 
 * @author Susovan
 */
function EditProductModal({show, onHide, itemDetails, setItemDetails}) {

  const [categories, setCategories] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  const [imageSource, setImageSource] = useState('url'); // 'url' or 'file'
const [imageError, setImageError] = useState('');
const fileRef = useRef(null);

  const getCategorises = async () => {
    try {
      const categories = await fetchCategories();
      console.log("Fetched categories:", categories);
      setCategories(categories.data);
    } catch (error) {
      handleErrorToast(error, "Failed to fetch categories");
    }
  }

  // Function to handle creating a new category
  const handleCreateNewCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Category name is required");
      return;
    }

    setIsCreatingCategory(true);
    try {
      const response = await createCategory({ name: newCategoryName.trim() });
      const newCategory = response.data;
      
      // Add the new category to the categories list
      setCategories(prev => [...prev, newCategory]);
      
      // Auto-select the newly created category
      setItemDetails({
        ...itemDetails,
        prodCategoryId: newCategory.id,
        prodCategoryName: newCategory.name,
        file: itemDetails.file,
      });
      
      // Reset the new category input
      setNewCategoryName("");
      setShowNewCategoryInput(false);
      
      toast.success("Category created successfully!");
    } catch (error) {
      handleErrorToast(error, "Failed to create category");
    } finally {
      setIsCreatingCategory(false);
    }
  };

  // Robust handler for image URL changes
  const handleImageUrlChange = (e) => {
    const newImageUrl = e.target.value;
    setImageError('');
    
    // Validate URL format if not empty
    if (newImageUrl && !isValidUrl(newImageUrl)) {
      setImageError('Please enter a valid URL');
      return;
    }
    
    setImageSource('url');
    setItemDetails({
      ...itemDetails,
      imageUrl: newImageUrl,
      file: null,
      previewUrl: null
    });
  };

  // Robust handler for file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageError('');
    
    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      setImageError('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setImageError('Image size should be less than 5MB');
      return;
    }

    try {
      const previewUrl = URL.createObjectURL(file);
      setImageSource('file');
      setItemDetails({
        ...itemDetails,
        file,
        imageUrl: "",
        previewUrl: previewUrl 
      });
    } catch (error) {
      setImageError('Failed to load image preview');
      console.error('Preview creation error:', error);
    }
  };

  // URL validation helper
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Handler to clear image
  const handleClearImage = () => {
    setImageSource('url');
    setImageError('');
    setItemDetails({
      ...itemDetails,
      imageUrl: "",
      file: null,
      previewUrl: null
    });
  };
  const handleCategoryChange = (value) => {
    if (value === "add_new") {
      setShowNewCategoryInput(true);
    } else {
      setShowNewCategoryInput(false);
      const selectedCategory = categories.find((cat) => cat.id === value);
      setItemDetails({
        ...itemDetails,
        prodCategoryId: value,
        prodCategoryName: selectedCategory?.name || "",
      });
    }
  };

  useEffect(() => {
    if (show) {
      getCategorises();
      // Determine current image source
      if (itemDetails.file) {
        setImageSource('file');
      } else if (itemDetails.imageUrl) {
        setImageSource('url');
      } else {
        setImageSource('url'); // Default to URL
      }
      setImageError('');
    }
  }, [show]);

  //Api handler for updating product details by product id
  const UpdateProduct = useCallback(async (id) =>{
    // Prevent updating product if currently creating a category
    if (isCreatingCategory) {
      toast.error("Please wait for category creation to complete");
      return;
    }
    
    // Check for image validation errors
    if (imageError) {
      toast.error("Please fix image errors before saving");
      return;
    }
    
    // Check if category is selected or being created
    if (!itemDetails.prodCategoryId && !showNewCategoryInput) {
      toast.error("Please select a category");
      return;
    }
    
    // Ensure we have the latest itemDetails state
    console.log("Updating product with data:", itemDetails);
    
    try {
      const res = await UpdateProductApi(id, itemDetails);
      toast.success(ITEM_TOASTS.ITEM_EDITED);
      onHide()
      // Reset category creation states
      setShowNewCategoryInput(false);
      setNewCategoryName("");
    }
    catch(error) {
      toast.error(ITEM_TOASTS.ITEM_NOT_EDITED);
    }
  }, [itemDetails, isCreatingCategory, showNewCategoryInput, onHide, imageError]);

  useEffect(() => {
  if (!show) return;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      UpdateProduct(itemDetails.id);
    }
  };

  window.addEventListener('keydown', handleKeyDown);

  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [show, UpdateProduct, itemDetails.id]); // Include itemDetails.id in dependencies

  return (
    <Modal
    show={show}
    onHide={onHide}
    size="lg"
    aria-labelledby="contained-modal-title-vcenter"
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title id="contained-modal-title-vcenter">
        Edit Product
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className={`px-5 ${style.modalScrollableBody}`}>
      <div className="row">
        <div className="col-md-7 d-flex flex-column">
          <div className="mb-3">
            <label htmlFor="productName" className="form-label">Product Name</label>
            <input value={itemDetails.prodName} onChange={(e) => setItemDetails({...itemDetails, prodName : e.target.value})}  type="text" className="form-control" id="productName" placeholder="Product Name" />
            { !itemDetails.prodName &&
              <span className='text-muted' style={{fontSize:"12px"}}>Product name is required!*</span>
             }
          </div>

          <div className='d-flex gap-3'>
          <div className="mb-3">
        <label htmlFor="productPrice" className="form-label">Price</label>
        <input
          value={itemDetails.prodPrice}
          onChange={(e) =>
            setItemDetails({ ...itemDetails, prodPrice: e.target.value })
          }
          type="number"
          className="form-control"
          id="productPrice"
          placeholder="Price in ₹"
        />
        { !itemDetails.prodPrice &&
          <span className='text-muted' style={{fontSize:"12px"}}>Product price is required!*</span>
        }
      </div>


      <div className="mb-3">
        <label htmlFor="productImage" className="form-label">Image Link</label>
            <input 
              value={itemDetails.imageUrl} 
              onChange={handleImageUrlChange} 
              type="text" 
              className={`form-control ${imageError ? 'is-invalid' : ''}`}
              id="productImage" 
              placeholder="Image URL" 
              disabled={imageSource === 'file'}
            />
            {imageError && (
              <div className="invalid-feedback d-block">
                {imageError}
              </div>
            )}
            {imageSource === 'file' && (
              <span className='text-muted' style={{fontSize:"12px"}}>Remove uploaded file to enter image URL</span>
            )}
      </div>
      </div>
      <div className='d-flex gap-3'>
      <div className="mb-3">
        <label htmlFor="productCategory" className="form-label">Category</label>
        <Form.Select
          value={showNewCategoryInput ? "add_new" : itemDetails.prodCategoryId}
          onChange={(e) => handleCategoryChange(e.target.value)}
          disabled={isCreatingCategory}
        >
          <option value="">Select category</option>
          {categories.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
          <option value="add_new">+ Add New Category</option>
        </Form.Select>
        
        {showNewCategoryInput && (
          <div className="mt-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center gap-2 mb-2">
              <Tag size={16} className="text-primary" />
              <label className="form-label mb-0">New Category Name</label>
            </div>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Enter category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                disabled={isCreatingCategory}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateNewCategory();
                  }
                }}
              />
              <Button
                variant="primary"
                onClick={handleCreateNewCategory}
                disabled={isCreatingCategory || !newCategoryName.trim()}
                size="sm"
              >
                {isCreatingCategory ? "Creating..." : "Add"}
              </Button>
            </div>
          </div>
        )}
        
        { !itemDetails.prodCategoryId && !showNewCategoryInput &&
          <span className='text-muted' style={{fontSize:"12px"}}>Product category is required!*</span>
        }
      </div>
      <div className='mb-3'>
        <label htmlFor="" className='form-label'>Stock Quantity</label>
        <input
          value={itemDetails.quantity}
          onChange={(e) =>
            setItemDetails({ ...itemDetails, quantity: e.target.value })
          }
          type="number"
          className="form-control"
          id="productQuantity"
          placeholder="Add Quantity"
        />
        { !itemDetails.quantity &&
          <span className='text-muted' style={{fontSize:"12px"}}>Product quantity is required!*</span>
        }
      </div>
      </div>

      <div className="mb-3">
        <label htmlFor="productType" className="form-label">Type</label>
            <Form.Select
              value={itemDetails.veg ? "veg" : "non-veg"}
              onChange={(e) => setItemDetails({ ...itemDetails, veg: e.target.value === "veg" })}
            >
              <option value="veg">Vegetarian</option>
              <option value="non-veg">Non-Vegetarian</option>
            </Form.Select>
          </div>
          <div className="mb-3">
            <label htmlFor="productDescription" className="form-label">Description</label>
            <textarea 
              value={itemDetails.description} 
              onChange={(e) => setItemDetails({...itemDetails, description: e.target.value})} 
              className="form-control" 
              id="productDescription" 
              rows="3"
            ></textarea>
          </div>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileRef}
          className="d-none"
          onChange={handleFileSelect}
        />
        <div className="col-md-5 d-flex justify-content-center pt-4 align-items-center">
        <div
          className={`${style.imageContainerDiv} border rounded d-flex justify-content-center align-items-center position-relative`}
          onClick={() => imageSource === 'url' && fileRef.current.click()}
          style={{ cursor: imageSource === 'url' ? "pointer" : "not-allowed" }}
        >
          {/* Show upload icon when no image, otherwise show image */}
          {!itemDetails.previewUrl && !itemDetails.imageUrl ? (
            <Upload size={60} className="text-muted" />
          ) : (
            <img
              style={{ objectFit: "cover" }}
              height={140}
              src={itemDetails.previewUrl || itemDetails.imageUrl}
              alt="Product preview"
            />
          )}
          
          {imageSource === 'url' && itemDetails.imageUrl && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50 text-white opacity-0 hover-opacity-100 transition-opacity">
              <small>Click to change image</small>
            </div>
          )}

          {(itemDetails.previewUrl || itemDetails.imageUrl) && (
            <>
              {/* Remove button overlay */}
              <div 
                className="position-absolute top-0 end-0 m-1 bg-danger rounded-circle d-flex align-items-center justify-content-center cursor-pointer hover-scale"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearImage();
                }}
                style={{
                  width: '28px',
                  height: '28px',
                  zIndex: 10,
                  transition: 'transform 0.2s'
                }}
                title="Remove image"
              >
                <X size={16} color="white" strokeWidth={3} />
              </div>
              
              {/* Hover overlay with remove text */}
              <div className={style.removeOverlay}>
                <div className="d-flex flex-column align-items-center justify-content-center h-100">
                  <X size={24} color="white" className="mb-2" />
                  <span className="text-white fw-bold">Remove Image</span>
                </div>
              </div>
            </>
          )}
          
          {imageError && (
            <div className="position-absolute bottom-0 start-0 w-100 bg-danger text-white text-center p-1">
              <small>{imageError}</small>
            </div>
          )}
        </div>
        </div>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button variant='danger' onClick={onHide}>Close</Button>
      <Button onClick={() => UpdateProduct(itemDetails.id)}>Save</Button>
    </Modal.Footer>
  </Modal>
  );
}
export default EditProductModal